import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { Row, Col, Panel, DropdownButton, Glyphicon, MenuItem, ButtonGroup, Well } from 'react-bootstrap';
import request from 'axios';
import { getClientAuthHeader } from '../../client/token';
import Table from './common/Table';
import ClientAvatar from './common/ClientAvatar';
import SearchForm, { navigateToClients } from './common/SearchForm';
import ConfirmDialog from './common/ConfirmDialog';
import EditTableHeaders from './common/EditTableHeaders';
import ImportDialog from './common/ImportDialog';
import Button from './common/Button';
import CheckBox from './common/CheckBox';
import { clientsUpdateColumnsAction,
         clientsLoadAction,
         clientsPagingAction,
         clientEditAction, 
         clientsDeleteAction} from '../actions/clientActions';

import { caseSetServerError } from '../actions/caseActions';
import { downloadCsv, filteredRoute, findBootstrapEnvironment, loadAction } from '../helpers/utils';
import Cards from './Cards';
import { deletedRowColor } from '../helpers/colors';

class Clients extends PureComponent {
    constructor(props) {
        super(props);
        this.onColumnResized = this.onColumnResized.bind(this);
        this.getSelectedClientIds = this.getSelectedClientIds.bind(this);
        this.editRow = this.editRow.bind(this);
        this.editTable = this.editTable.bind(this);
        this.editTableConfirm = this.editTableConfirm.bind(this);
        this.archiveSelected = this.archiveSelected.bind(this);
        this.archiveDialog = this.archiveDialog.bind(this);
        this.checkRow = this.checkRow.bind(this);
        this.downloadSelectedAsCsv = this.downloadSelectedAsCsv.bind(this);
        this.refreshIfNeeded = this.refreshIfNeeded.bind(this);
        this.onPagingChange = this.onPagingChange.bind(this);
        this.editTableHeadersRef = React.createRef();
        this.state = {
            dialogTitle: '',
            dialogText: '',
            dialogConfirm: undefined,
            dialogOpen: false,
            dialogSize: undefined,
            dialogWarning: false,
            dialogChildren: undefined,
            dialogPermanentlyDelete: false,
            loading: false,
            importDialogOpen: false,
            checkedRows: {},
        };
    }

    editRow(_rowIndex, currentClient) {
        let id = parseInt(currentClient.client.id);
        this.props.dispatch(clientEditAction(currentClient.client));
        browserHistory.push('/client/edit/' + id);
    }

    editTable() {
        let fields = [];
        if (this.props.workflow && this.props.client) {
            fields = [
                ...this.props.workflow,
                ...this.props.client,
                {
                   "type": "Client",
                   "tables": [
                       {
                            "table": "client",
                            "fields": [{
                                "name": "ClientType",
                                "type": "ClientType",
                                "props": {
                                    "label": "Client Type"
                                }
                            }]
                       }
                    ]
                }
            ];
        }

        this.setState({
            dialogTitle: 'Edit Columns',
            dialogText: <Well>Choose which columns should be visible and what should be their order. Hold CTRL (PC) / CMD (Mac) key when clicking to select multiple columns.</Well>,
            dialogSize: 'tall',
            dialogWarning: false,
            dialogConfirm: () => this.editTableConfirm(),
            dialogOpen: true,
            dialogChildren: 
                <EditTableHeaders
                    activeHeaders={this.props.headers}
                    workflow={fields}
                    filterTable='client'
                    ref={this.editTableHeadersRef}
                />
        });
    }

    editTableConfirm() {
        let activeHeaders = this.refs.EditTableHeaders.getActiveHeaders();
        this.props.dispatch(clientsUpdateColumnsAction(activeHeaders))
            .then(_result => {
                this.setState({dialogOpen: false});
            })
            .catch((error) => {
                console.error(new Error().stack, error);
                this.props.dispatch(caseSetServerError(error));
            });
    }

    archiveDialog() {
        this.setState({
            dialogTitle: 'Archive Contacts',
            dialogText: 'Do you really want to archive the selected contacts? Note that if the contact is already archived, it will be unarchived.',
            dialogSize: undefined,
            dialogWarning: true,
            dialogConfirm: () => {
                this.archiveSelected(this.state.dialogPermanentlyDelete);
                this.setState({dialogOpen: false, checkedRows: {}});
            },
            dialogOpen: true,
            dialogPermanentlyDelete: false,
            dialogChildren:
                <Row>
                <br />
                <Col xs={12}>
                <CheckBox 
                    field={{
                        name: 'permanentlyDeleteCheckbox'
                    }}
                    label="Permanently delete"
                    help="If checked, the contacts will be permanently deleted."
                    onChange={(_elem) => {
                        this.setState({dialogPermanentlyDelete: !this.state.dialogPermanentlyDelete});
                    }}
                />
                </Col>
                </Row>
        });
    }

    checkRow(_rows, indexes) {
        let checkedRows;
        if (indexes === undefined) {
            checkedRows = {};
        } else {
            checkedRows = {...this.state.checkedRows};
            for (let i = 0; i < indexes.length; i++) {
                const index = indexes[i];
                checkedRows[index] = !checkedRows[index];
            }
        }

        this.setState({checkedRows});
    }

    getSelectedClientIds() {
        const ids = [];
        if (!this.props.rows) {
            return ids;
        }

        for (const checked in this.state.checkedRows) {
            if (this.state.checkedRows[checked]) {
                const rowId = parseInt(checked, 10);
                ids.push(this.props.rows[rowId].client.id);
            }
        }

        return ids;
    }

    async archiveSelected(permanently) {
        this.setState({loading: true});
        const ids = this.getSelectedClientIds();
        try {
            await this.props.dispatch(clientsDeleteAction(ids, permanently))
        } catch (error) {
            console.error(new Error().stack, error);
            this.props.dispatch(caseSetServerError(error));
        }

        this.setState({loading: false});
    }

    async downloadSelectedAsCsv() {
        let { rows } = this.props;
        let headers = [...this.props.headers];
        const ids = this.getSelectedClientIds();
        headers = headers.map(h => {
            h.table = '';
            return h;
        });

        if (ids.length > 0) {
            // Selection is always within the loaded page.
            rows = rows.filter(r => ids.includes(r.client.id));
            downloadCsv(headers, rows, 'contacts.csv');
            return;
        }

        // Nothing selected: export every contact matching the current search, not just this page.
        try {
            const { q, f, a } = this.props.query;
            const result = await request.get(loadAction('/api/v1/client/all', q, f, a), getClientAuthHeader());
            downloadCsv(headers, result.data.clients, 'contacts.csv', 'client');
        } catch (error) {
            console.error(new Error().stack, error);
            this.props.dispatch(caseSetServerError(error));
        }
    }

    onPagingChange(paging) {
        this.props.dispatch(clientsPagingAction(paging));
    }

    refreshIfNeeded(props) {
        if (props.refreshNeeded && props.query) {
            let query = props.query.q;
            let filter = props.query.f;
            let archived = props.query.a;

            // A new search or tile starts at the first page.
            let paging = props.paging;
            const querySignature = JSON.stringify([query, filter, archived]);
            if (this.querySignature !== undefined && this.querySignature !== querySignature && paging.page !== 0) {
                paging = { ...paging, page: 0, append: false };
            } else if (!paging.append && paging.page !== 0) {
                // A plain reload starts again from the first page; only scrolling appends.
                paging = { ...paging, page: 0 };
            }

            this.querySignature = querySignature;
            this.props.dispatch(clientsLoadAction(query, filter, archived, paging))
                .catch((error) => {
                    console.error(new Error().stack, error);
                    this.props.dispatch(caseSetServerError(error));
                });
        }
    }

    componentDidMount() {
        this.refreshIfNeeded(this.props);
    }

    componentWillReceiveProps(nextProps) {
        // Rows appended by scrolling keep the current selection.
        const appended = this.props.rows && nextProps.rows && nextProps.rows.length > this.props.rows.length &&
            nextProps.rows[0] === this.props.rows[0];
        if (this.props.rows != nextProps.rows && !appended) {
            this.setState({checkedRows: {}});
        }

        this.refreshIfNeeded(nextProps);
    }

    onColumnResized(newHeaders) {
        const persistable = (newHeaders || []).filter(h => !(h && h.name === '$ProfilePictureUrl$'));
        this.props.dispatch(clientsUpdateColumnsAction(persistable))
            .catch((error) => {
                console.error(new Error().stack, error);
                this.props.dispatch(caseSetServerError(error));
            });
    }

    render() {
        const {
            headers,
            rows,
            paging,
            total,
        } = this.props;

        const csvHeaders = [{ label: 'Case Tempo ID {id}', name: 'id', table: 'client'}];
        for (let i = 0; i < (headers || []).length; i++) {
            const obj = { ...headers[i] };
            obj.label = `${obj.label} {${obj.name}}`;
            csvHeaders.push(obj);
        }

        const avatarHeader = {
            table: 'client',
            name: '$ProfilePictureUrl$',
            label: '',
            width: 48,
            maxWidth: 48,
            minWidth: 48,
            sortable: false,
            resizable: false,
            filterable: false,
            cellRender: (row) => {
                const client = row && row.original && row.original.client;
                if (!client) {
                    return null;
                }

                return (
                    <div style={{ textAlign: 'center', paddingTop: 2 }} onClick={(e) => e.stopPropagation()}>
                        <ClientAvatar client={client} size={32} editable={!!(client && client.id)} />
                    </div>
                );
            },
        };
        const tableHeaders = [avatarHeader, ...(headers || [])];
        const sortField = paging.sort ? paging.sort.split('.').pop() : undefined;
        const sortedColumn = sortField ? tableHeaders.findIndex(h => h.name === sortField) : -1;

        const size = findBootstrapEnvironment();
        const noContactSelected = this.getSelectedClientIds().length === 0;
        const header = (
            <div style={{ height: 25 }}>
                <h6 style={{ float: 'left', marginTop: 5 }}>{`Contacts (${!rows ? 0 : total})`}</h6>
                <div style={{ float: 'right' }}>
                <ButtonGroup style={{ boxShadow: 'none', marginTop: -8, marginRight: 0, marginBottom: -1, backgroundColor: 'rgba(0,0,0,0)' }}>
                    <Button
                        onClick={this.archiveDialog}
                        disabled={noContactSelected}
                        icon="inbox"
                        tooltip="Archive selected"
                        tooltipPosition="bottom"
                        style={{backgroundColor: 'rgba(0,0,0,0)'}}
                    />
                </ButtonGroup>
                <DropdownButton title={<Glyphicon glyph="option-vertical" />}
                                noCaret
                                pullRight
                                style={{ boxShadow: 'none', marginTop: -8, marginRight: -15, marginBottom: -1, backgroundColor: 'rgba(0,0,0,0)' }}
                >
                <MenuItem eventKey="downloadCsv" onClick={() => this.downloadSelectedAsCsv()}>Export to CSV</MenuItem>
                <MenuItem eventKey="uploadCsv" onClick={() => this.setState({ importDialogOpen: true })}>Import from CSV</MenuItem>
                </DropdownButton>
                </div>
            </div>);

        const output = (
            <div className="ct-page">
            {this.props.cards && this.props.cards.length > 1 && (
            <Row>
            <Col xs={12}>
                <Cards onClick={(state) => {
                           const route = filteredRoute('/client/all',
                                                       navigateToClients(this.props.query && this.props.query.hideFilterPanel,
                                                                         state));

                           browserHistory.push(route);
                       }}
                       cards={this.props.cards}
                       cardStats={this.props.cardStats}
                       cardGlyph="user"
                       selectedCard={this.props.selectedCard}
                />
            </Col>
            </Row>)}
            <Row>
            <Col xs={12} id="contactsTable" style={{ marginRight: 0 }}>
            <Panel data-hj-whitelist header={header} style={{ margin: 0 }}>
            <Row>
                <Col xs={12}>
                    <SearchForm {...this.props}
                        recordType="client"
                    />
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <Table
                        headers={tableHeaders}
                        rows={rows}
                        buttonsWidth={20}
                        onColumnResized={this.onColumnResized}
                        editRow={this.editRow}
                        editBsStyle="link"
                        editTable={this.editTable}
                        checkRow={this.checkRow}
                        checkedRows={this.state.checkedRows}
                        filterable={false}
                        defaultPageSize={10}
                        serverPaging={{
                            pages: Math.max(Math.ceil(total / paging.pageSize), 1),
                            page: paging.page,
                            pageSize: paging.pageSize,
                            sorted: sortedColumn >= 0 ? [{ id: sortedColumn, desc: paging.desc }] : [],
                            onChange: this.onPagingChange,
                            loadingMore: !!paging.append && this.props.loadingPage,
                        }}
                        loading={this.state.loading || this.props.loadingPage || typeof(this.props.rows) === 'undefined'}
                        getTdProps={(state, rowInfo, column, instance, props) => {
                            let style = {};
                            if (rowInfo && rowInfo.original && rowInfo.original.client['deleted_at']) {
                                style = { backgroundColor: deletedRowColor };
                            }
                    
                            return {
                                onClick: (e, handleOriginal) => {
                                    if (column.id === 'check') {
                                        return;
                                    }
                                    props.editRow(rowInfo.index, rowInfo.original);
                                },
                                style
                            };
                        }}
                    />
                </Col>
            </Row>
            </Panel>
            </Col>
            </Row>
            <ImportDialog 
                open={this.state.importDialogOpen}
                cancel={() => this.setState({ importDialogOpen: false })}
                title='Importing Contacts'
                type='contact'
            />
            </div>
        );

        return (
            <div>
                {output}
                <ConfirmDialog
                    title={this.state.dialogTitle}
                    warning={this.state.dialogWarning}
                    text={this.state.dialogText}
                    cancel={() => {this.setState({dialogOpen: false})}}
                    confirm={this.state.dialogConfirm}
                    open={this.state.dialogOpen}
                    size={this.state.dialogSize}
                >
                {this.state.dialogChildren}
                </ConfirmDialog>
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    let headers = [];
    let workflow;
    let client;
    const config = state.user.config;
    let cards = [];
    const cardStats = (state.user && state.user.stats && state.user.stats.client) || {};
    if (config) {
        headers = [...state.user.config.clientHeaders];
        workflow = [...state.user.config.workflow];
        client = [...state.user.config.client];
        cards =  config.client.map(c => c.type);
    }

    Object.keys(cardStats).forEach((key) => {
        if (!cards.find(v => v === key)) {
            if (typeof(key) !== 'string') {
                key = JSON.stringify(key);
            }

            cards.push(key);
        }
    });

    const cardQuery = state.routing.locationBeforeTransitions.query;
    let selectedCard;
    if (cardQuery.hideFilterPanel !== 'false' && cardQuery.hideFilterPanel !== 'true') {
        selectedCard = cardQuery.hideFilterPanel;
    }

    return {
        headers,
        workflow,
        client,
        rows: state.clients.tableRows,
        total: state.clients.total,
        paging: state.clients.paging,
        loadingPage: state.clients.loadingPage,
        query: state.routing.locationBeforeTransitions.query,
        refreshNeeded: state.clients.refreshNeeded,
        cards,
        cardStats,
        selectedCard,
    };
};

export default connect(mapStateToProps)(Clients);
